// The Control Plane workflow, drawn.
//
// Draft → Validate → Review → Publish is the real sequence a change moves
// through, and until now an operator had to infer it from which buttons
// happened to be enabled on which of four routes. The stepper states it.
//
// ─── What this component deliberately is NOT ──────────────────────────────
//
// It is a NAVIGATION path, not a status ledger. Only two of the four steps
// have server-backed state this page can honestly read:
//
//   Draft    — `draft.matchesPublished` says whether there are pending changes
//   Publish  — the live revision exists, or it does not
//
// Validate and Review have no persisted result. `validateDraft` is called on
// the Review screen and its outcome is not stored anywhere; "reviewed" is not
// a thing the backend records at all. So those two steps render as reachable
// or not reachable — never as "done" — because a green tick against a step
// nobody performed is exactly the kind of confident lie this console must not
// tell about a publish pipeline.

import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import type { Tone } from "./tone";
import { IconCheck } from "./icons";

export type StepState = "done" | "current" | "available" | "blocked";

export interface WorkflowStep {
  key: string;
  label: string;
  /** Where the step happens. Omitted when the step is not reachable yet. */
  to?: string;
  state: StepState;
  /** One short line of real state, or nothing. Never invented. */
  hint?: string;
  icon?: ReactNode;
}

const STATE_TONE: Record<StepState, Tone> = {
  done: "ok",
  current: "accent",
  available: "neutral",
  blocked: "neutral",
};

function StepMarker({ index, state }: { index: number; state: StepState }) {
  const tone = STATE_TONE[state];
  const filled = state === "done" || state === "current";
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 28,
        height: 28,
        flex: "0 0 auto",
        borderRadius: "var(--r-pill)",
        border: `1px solid var(--${tone === "neutral" ? "border-strong" : tone}${
          tone === "neutral" ? "" : "-border"
        })`,
        background: filled ? `var(--${tone === "neutral" ? "bg-inset" : tone}${tone === "neutral" ? "" : "-bg"})` : "var(--bg-inset)",
        color: tone === "neutral" ? "var(--fg-dim)" : `var(--${tone})`,
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {state === "done" ? <IconCheck size={14} /> : index + 1}
    </span>
  );
}

export function WorkflowStepper({
  steps,
  label,
}: {
  steps: ReadonlyArray<WorkflowStep>;
  label: string;
}) {
  return (
    <nav
      aria-label={label}
      style={{
        display: "flex",
        alignItems: "stretch",
        flexWrap: "wrap",
        gap: 0,
        border: "1px solid var(--border)",
        borderRadius: "var(--r-lg)",
        background: "var(--bg-card)",
        overflow: "hidden",
      }}
    >
      {steps.map((step, i) => {
        const reachable = step.to !== undefined && step.state !== "blocked";
        const body = (
          <>
            <StepMarker index={i} state={step.state} />
            <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <span
                style={{
                  fontSize: 13.5,
                  fontWeight: step.state === "current" ? 660 : 560,
                  color:
                    step.state === "blocked"
                      ? "var(--fg-dim)"
                      : step.state === "current"
                        ? "var(--accent)"
                        : "var(--fg)",
                }}
              >
                {step.label}
              </span>
              {step.hint !== undefined && (
                <span style={{ fontSize: 11.5, color: "var(--fg-dim)" }}>{step.hint}</span>
              )}
            </span>
          </>
        );

        const shared = {
          style: {
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "14px 16px",
            flex: "1 1 190px",
            minWidth: 0,
            borderRight: i < steps.length - 1 ? "1px solid var(--border-faint)" : undefined,
            background: step.state === "current" ? "var(--accent-bg)" : "transparent",
            textDecoration: "none",
            color: "inherit",
          } as const,
        };

        return reachable ? (
          <Link key={step.key} to={step.to!} className="g-row" {...shared}>
            {body}
          </Link>
        ) : (
          <div key={step.key} {...shared} aria-disabled={step.state === "blocked"}>
            {body}
          </div>
        );
      })}
    </nav>
  );
}

/** A large workflow control. One per real action, side by side, so the four
 *  verbs an operator came to perform are the first thing on the page rather
 *  than something to be inferred from which buttons happen to be enabled. */
export function ActionCard({
  label,
  hint,
  icon,
  to,
  onClick,
  tone = "neutral",
  disabled,
  disabledReason,
}: {
  label: string;
  hint?: string;
  icon: ReactNode;
  to?: string;
  onClick?: () => void;
  tone?: Tone;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const inner = (
    <>
      <span
        aria-hidden="true"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 34,
          height: 34,
          borderRadius: "var(--r-md)",
          border: `1px solid var(--${tone === "neutral" ? "border" : `${tone}-border`})`,
          background: tone === "neutral" ? "var(--bg-inset)" : `var(--${tone}-bg)`,
          color: tone === "neutral" ? "var(--fg-dim)" : `var(--${tone})`,
          flex: "0 0 auto",
        }}
      >
        {icon}
      </span>
      <span style={{ display: "flex", flexDirection: "column", minWidth: 0, textAlign: "left" }}>
        <span style={{ fontSize: 14, fontWeight: 640, color: "var(--fg)" }}>{label}</span>
        {(disabled ? disabledReason : hint) !== undefined && (
          <span style={{ fontSize: 11.5, color: "var(--fg-dim)" }}>
            {disabled ? disabledReason : hint}
          </span>
        )}
      </span>
    </>
  );

  const style = {
    display: "flex",
    alignItems: "center",
    gap: 11,
    // 44px+ per the operator's minimum target size for a major action.
    minHeight: 66,
    padding: "12px 14px",
    width: "100%",
    borderRadius: "var(--r-md)",
    border: "1px solid var(--border)",
    background: "var(--bg-card)",
    textDecoration: "none",
    color: "inherit",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    textAlign: "left" as const,
    font: "inherit",
  };

  if (disabled || (to === undefined && onClick === undefined)) {
    return (
      <div style={style} aria-disabled="true" title={disabledReason}>
        {inner}
      </div>
    );
  }
  return to !== undefined ? (
    <Link to={to} className="g-row" style={style}>
      {inner}
    </Link>
  ) : (
    <button type="button" onClick={onClick} className="g-row" style={style}>
      {inner}
    </button>
  );
}

export function ActionRow({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(232px, 1fr))",
        gap: 10,
      }}
    >
      {children}
    </div>
  );
}
